import React, { useEffect, useState } from 'react';
import './AddRecommendationIntermediary.css';
import InputMask from 'react-input-mask';
import jwtDecode from 'jwt-decode';
import { getCookie } from 'cookies-next';
import { INTERMEDIARY_TOKEN_COOKIE } from '../../utils/authCookies';
import {
    deleteDocRapidoQuotaById,
    editDocRapidoQuotaById,
    editDocRapidoQuotasAcquisitionById,
    getBanks,
    getConsortiumAgencies,
    getDocRapidoQuotasAcquisitionById,
    getDocRapidoQuotaById,
    newDocRapidoQuota,
    newDocRapidoQuotasAcquisition,
} from '../../utils/api';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-toastify';
import Modal from 'react-modal';

const emptyPerson = {
    name: '',
    phone: '',
    nationality: '',
    maritalStatus: '',
    profession: '',
    state: '',
    address: '',
    number: '',
    neighborhood: '',
    city: '',
    zipCode: '',
    complement: '',
    notaryCity: '',
    cpf: '',
    assignmentValue: '',
};

const emptyCompany = {
    cnpj: '',
    corporateName: '',
    address: '',
    number: '',
    neighborhood: '',
    city: '',
    zipCode: '',
    state: '',
    complement: '',
};

const emptyQuota = {
    group: '',
    quota: '',
    contract: '',
    creditValue: '',
    commonFundPercent: '',
    receivableValue: '',
    notaryCity: '',
};

const emptyBank = {
    pix: '',
    pixType: '',
    bank: '',
    agency: '',
    account: '',
    accountType: '',
    holderName: '',
    holderDocument: '',
    useFavoredDataForPayment: false,
};

const emptyRegistry = {
    notaryCity: '',
    assignmentValue: '',
    signatureRecognition: false,
};

const defaultAdministrators = [
    {
        name: 'Outra',
        cnpj: '',
        address: '',
        number: '',
        neighborhood: '',
        city: '',
        zipCode: '',
        state: '',
    },
];

const normalizeConsortiumAgency = (agency) => ({
    id: agency.id || agency.consortium_agency_id || agency.agency_id || '',
    name: agency.name || agency.agency_name || agency.companyName || agency.corporateName || agency.razaoSocial || agency.razao_social || '',
    cnpj: agency.cnpj || agency.document || agency.document_number || '',
    address: agency.address || agency.street || agency.logradouro || '',
    number: agency.number || agency.address_number || agency.numero || '',
    neighborhood: agency.neighborhood || agency.district || agency.bairro || '',
    city: agency.city || agency.localidade || agency.cidade || '',
    zipCode: agency.zipCode || agency.zipcode || agency.zip_code || agency.cep || '',
    state: agency.state || agency.uf || agency.estado || '',
});

const normalizeBank = (bank) => ({
    id: bank.id || bank.bank_id || bank.code || bank.codigo || '',
    name: bank.name || bank.bank_name || bank.nome || bank.label || '',
});

const brazilianStates = [
    'AC',
    'AL',
    'AP',
    'AM',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MT',
    'MS',
    'MG',
    'PA',
    'PB',
    'PR',
    'PE',
    'PI',
    'RJ',
    'RN',
    'RS',
    'RO',
    'RR',
    'SC',
    'SP',
    'SE',
    'TO',
];

const createInitialForm = () => ({
    assignorType: 'cpf',
    assignorPerson: { ...emptyPerson },
    assignorCompany: { ...emptyCompany },
    representative: { ...emptyPerson },
    administrator: { ...emptyCompany, name: '' },
    quotas: [{ ...emptyQuota }],
    bank: { ...emptyBank },
    registry: { ...emptyRegistry },
});

const steps = [
    'Cedente',
    'Dados bancários',
    'Administradora',
    'Dados da cota',
    'Cartório e cessão',
];

const parseCurrency = (value) => {
    if (!value) {
        return 0;
    }

    return Number(value.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
};

const formatDecimal = (value) => {
    if (!value) {
        return '';
    }

    return String(parseCurrency(value).toFixed(2));
};

const normalizeId = (value) => {
    if (value === '' || value === null || value === undefined) {
        return '';
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? value : numericValue;
};

const parsePercent = (value) => {
    if (!value) {
        return 0;
    }

    return Number(value.replace(',', '.')) || 0;
};

const formatCurrency = (value) => {
    return value.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
    });
};

const formatMoneyField = (value) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const numericValue = Number(String(value).replace(',', '.'));

    if (Number.isNaN(numericValue)) {
        return '';
    }

    return formatCurrency(numericValue);
};

const formatPercentField = (value) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    return String(value).replace('.', ',');
};

const getResponseData = (response) => response?.data || response?.acquisition || response?.quota || response;

const mapAcquisitionToForm = (acquisition) => {
    const isCnpj = acquisition.doc_type === 'CNPJ';

    return {
        assignorType: isCnpj ? 'cnpj' : 'cpf',
        assignorPerson: {
            ...emptyPerson,
            name: isCnpj ? '' : acquisition.name || '',
            phone: isCnpj ? '' : acquisition.phone_number || '',
            nationality: isCnpj ? '' : acquisition.nationality || '',
            maritalStatus: isCnpj ? '' : acquisition.marital_status || '',
            profession: isCnpj ? '' : acquisition.occupation || '',
            state: isCnpj ? '' : acquisition.address_state || '',
            address: isCnpj ? '' : acquisition.address_street || '',
            number: isCnpj ? '' : acquisition.address_number || '',
            neighborhood: isCnpj ? '' : acquisition.address_neighborhood || '',
            city: isCnpj ? '' : acquisition.address_city || '',
            zipCode: isCnpj ? '' : acquisition.address_cep || '',
            complement: isCnpj ? '' : acquisition.address_complement || '',
            cpf: isCnpj ? '' : acquisition.cpf_number || '',
        },
        assignorCompany: {
            ...emptyCompany,
            cnpj: acquisition.cnpj_number || '',
            corporateName: acquisition.bussiness_name || '',
            address: acquisition.cnpj_address_street || '',
            number: acquisition.cnpj_address_number || '',
            neighborhood: acquisition.cnpj_address_neighborhood || '',
            city: acquisition.cnpj_address_city || '',
            zipCode: acquisition.cnpj_address_cep || '',
            state: acquisition.cnpj_address_state || '',
            complement: acquisition.cnpj_address_complement || '',
        },
        representative: {
            ...emptyPerson,
            name: isCnpj ? acquisition.name || '' : '',
            phone: isCnpj ? acquisition.phone_number || '' : '',
            nationality: isCnpj ? acquisition.nationality || '' : '',
            maritalStatus: isCnpj ? acquisition.marital_status || '' : '',
            profession: isCnpj ? acquisition.occupation || '' : '',
            state: isCnpj ? acquisition.address_state || '' : '',
            address: isCnpj ? acquisition.address_street || '' : '',
            number: isCnpj ? acquisition.address_number || '' : '',
            neighborhood: isCnpj ? acquisition.address_neighborhood || '' : '',
            city: isCnpj ? acquisition.address_city || '' : '',
            zipCode: isCnpj ? acquisition.address_cep || '' : '',
            complement: isCnpj ? acquisition.address_complement || '' : '',
            cpf: isCnpj ? acquisition.cpf_number || '' : '',
        },
        administrator: {
            ...emptyCompany,
            id: acquisition.consortium_agency_id || '',
            name: acquisition.consortium_agency?.name || acquisition.consortium_agency_name || acquisition.consortium_agency_fullname || acquisition.administrator_name || acquisition.agency_name || '',
            cnpj: acquisition.consortium_agency?.cnpj || acquisition.consortium_agency_cnpj || acquisition.administrator_cnpj || '',
            address: acquisition.consortium_agency?.address || acquisition.consortium_agency_address || acquisition.administrator_address || '',
            number: acquisition.consortium_agency?.number || acquisition.consortium_agency_number || acquisition.administrator_number || '',
            neighborhood: acquisition.consortium_agency?.neighborhood || acquisition.consortium_agency_neighborhood || acquisition.administrator_neighborhood || '',
            city: acquisition.consortium_agency?.city || acquisition.consortium_agency_city || acquisition.administrator_city || '',
            zipCode: acquisition.consortium_agency?.zipCode || acquisition.consortium_agency?.zipcode || acquisition.consortium_agency_zip_code || acquisition.consortium_agency_cep || acquisition.administrator_zip_code || '',
            state: acquisition.consortium_agency?.state || acquisition.consortium_agency_state || acquisition.administrator_state || '',
        },
        quotas: acquisition.quotas?.length
            ? acquisition.quotas.map((quota) => ({
                id: quota.id,
                group: quota.group_number || '',
                quota: quota.quota_number || '',
                contract: quota.contract_number || '',
                creditValue: formatMoneyField(quota.credit_value),
                commonFundPercent: formatPercentField(quota.paid_percentage_value),
                receivableValue: formatMoneyField(quota.receivable_value),
            }))
            : [{ ...emptyQuota }],
        bank: {
            ...emptyBank,
            pix: acquisition.key_pix || '',
            pixType: acquisition.key_pix_type || '',
            bank: acquisition.bank_account_id || '',
            agency: acquisition.bank_account_agency || '',
            account: acquisition.bank_account_number || '',
            accountType: acquisition.bank_account_type || '',
            holderName: acquisition.bank_account_name || '',
            holderDocument: acquisition.bank_account_doc_number || '',
        },
        registry: {
            ...emptyRegistry,
            notaryCity: acquisition.registry_city || '',
            assignmentValue: formatMoneyField(acquisition.customer_value),
            signatureRecognition: acquisition.check_firma === 'S',
        },
    };
};

function AddRecommendationIntermediary({ isOpen, closeModal, acquisitionToEdit = null }) {
    const [form, setForm] = useState(createInitialForm());
    const [currentStep, setCurrentStep] = useState(0);
    const [administrators, setAdministrators] = useState(defaultAdministrators);
    const [banks, setBanks] = useState([]);
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
    const [loadingEditData, setLoadingEditData] = useState(false);
    const [deletingQuotaId, setDeletingQuotaId] = useState(null);
    const [quotaToDelete, setQuotaToDelete] = useState(null);
    const isEditMode = Boolean(acquisitionToEdit?.id);

    Modal.setAppElement('#root');

    useEffect(() => {
        async function loadConsortiumAgencies() {
            try {
                const response = await getConsortiumAgencies();
                const agencies = Array.isArray(response) ? response : response?.data || [];
                const normalizedAgencies = agencies
                    .map(normalizeConsortiumAgency)
                    .filter((agency) => agency.name);

                setAdministrators([
                    ...normalizedAgencies,
                    ...defaultAdministrators,
                ]);
            } catch (error) {
                setAdministrators(defaultAdministrators);
            }
        }

        async function loadBanks() {
            try {
                const response = await getBanks();
                const bankList = Array.isArray(response) ? response : response?.data || [];
                const normalizedBanks = bankList
                    .map(normalizeBank)
                    .filter((bank) => bank.id && bank.name);

                setBanks(normalizedBanks);
            } catch (error) {
                setBanks([]);
            }
        }

        if (isOpen) {
            loadConsortiumAgencies();
            loadBanks();
        }
    }, [isOpen]);

    useEffect(() => {
        async function loadEditData() {
            if (!isOpen || !isEditMode) {
                return;
            }

            setLoadingEditData(true);

            try {
                const response = await getDocRapidoQuotasAcquisitionById(acquisitionToEdit.id);
                const acquisition = getResponseData(response) || acquisitionToEdit;
                const quotasFromAcquisition = acquisition.quotas?.length
                    ? acquisition.quotas
                    : acquisitionToEdit.quotas || [];

                const quotas = await Promise.all(
                    quotasFromAcquisition.map(async (quota) => {
                        if (!quota.id) {
                            return quota;
                        }

                        try {
                            return getResponseData(await getDocRapidoQuotaById(quota.id));
                        } catch (error) {
                            return quota;
                        }
                    })
                );

                setForm(mapAcquisitionToForm({ ...acquisition, quotas }));
                setCurrentStep(0);
            } catch (error) {
                toast.error('Nao foi possivel carregar os dados da intermediacao.');
                setForm(mapAcquisitionToForm(acquisitionToEdit));
            } finally {
                setLoadingEditData(false);
            }
        }

        if (isOpen && !isEditMode) {
            setForm(createInitialForm());
            setCurrentStep(0);
        }

        loadEditData();
    }, [acquisitionToEdit, isEditMode, isOpen]);

    const updateForm = (field, value) => {
        setForm((currentForm) => ({
            ...currentForm,
            [field]: value,
        }));
    };

    const updateSection = (section, field, value) => {
        setForm((currentForm) => ({
            ...currentForm,
            [section]: {
                ...currentForm[section],
                [field]: value,
            },
        }));
    };

    const updateQuota = (index, field, value) => {
        setForm((currentForm) => ({
            ...currentForm,
            quotas: currentForm.quotas.map((quota, quotaIndex) => {
                if (quotaIndex !== index) {
                    return quota;
                }

                const updatedQuota = { ...quota, [field]: value };

                if (field === 'creditValue' || field === 'commonFundPercent') {
                    const creditValue = parseCurrency(updatedQuota.creditValue);
                    const percentValue = parsePercent(updatedQuota.commonFundPercent);

                    updatedQuota.receivableValue = creditValue && percentValue
                        ? formatCurrency((creditValue * percentValue) / 100)
                        : '';
                }

                return updatedQuota;
            }),
        }));
    };

    const fillAddressByZipCode = async (section, zipCode) => {
        const cleanZipCode = zipCode.replace(/\D/g, '');

        if (cleanZipCode.length !== 8) {
            return;
        }

        try {
            const response = await fetch(`https://viacep.com.br/ws/${cleanZipCode}/json/`);
            const data = await response.json();

            if (data.erro) {
                toast.warning('CEP nao encontrado.');
                return;
            }

            setForm((currentForm) => ({
                ...currentForm,
                [section]: {
                    ...currentForm[section],
                    address: data.logradouro || currentForm[section].address,
                    neighborhood: data.bairro || currentForm[section].neighborhood,
                    city: data.localidade || currentForm[section].city,
                    state: data.uf || currentForm[section].state,
                },
            }));
        } catch (error) {
            toast.warning('Nao foi possivel buscar o CEP.');
        }
    };

    useEffect(() => {
        if (!form.administrator.id || form.administrator.name) {
            return;
        }

        const selectedAdministrator = administrators.find((administrator) => (
            String(administrator.id) === String(form.administrator.id)
        ));

        if (!selectedAdministrator) {
            return;
        }

        setForm((currentForm) => ({
            ...currentForm,
            administrator: {
                ...currentForm.administrator,
                ...selectedAdministrator,
                id: selectedAdministrator.id,
            },
        }));
    }, [administrators, form.administrator.id, form.administrator.name]);

    const selectAdministrator = (administratorId) => {
        const selectedAdministrator = administrators.find((administrator) => (
            String(administrator.id || administrator.name) === String(administratorId)
        ));

        setForm((currentForm) => ({
            ...currentForm,
            administrator: {
                ...currentForm.administrator,
                ...(selectedAdministrator || {}),
                id: selectedAdministrator?.id || '',
                name: selectedAdministrator?.name || '',
            },
        }));
    };

    const toggleUseFavoredDataForPayment = (checked) => {
        setForm((currentForm) => {
            if (!checked) {
                return {
                    ...currentForm,
                    bank: {
                        ...currentForm.bank,
                        useFavoredDataForPayment: false,
                    },
                };
            }

            const isCpfAssignor = currentForm.assignorType === 'cpf';
            const favoredName = isCpfAssignor
                ? currentForm.assignorPerson.name
                : currentForm.assignorCompany.corporateName;

            return {
                ...currentForm,
                    bank: {
                        ...currentForm.bank,
                        useFavoredDataForPayment: true,
                        holderName: favoredName,
                        holderDocument: isCpfAssignor ? currentForm.assignorPerson.cpf : currentForm.assignorCompany.cnpj,
                    },
            };
        });
    };

    const updatePixType = (pixType) => {
        setForm((currentForm) => ({
            ...currentForm,
            bank: {
                ...currentForm.bank,
                pixType,
                pix: '',
            },
        }));
    };

    const addQuota = () => {
        setForm((currentForm) => ({
            ...currentForm,
            quotas: [...currentForm.quotas, { ...emptyQuota }],
        }));
    };

    const removeQuotaFromForm = (index) => {
        setForm((currentForm) => ({
            ...currentForm,
            quotas: currentForm.quotas.filter((_, quotaIndex) => quotaIndex !== index),
        }));
    };

    const requestRemoveQuota = (index) => {
        const quotaToRemove = form.quotas[index];

        if (quotaToRemove?.id) {
            setQuotaToDelete({ ...quotaToRemove, index });
            return;
        }

        removeQuotaFromForm(index);
    };

    const cancelRemoveQuota = () => {
        setQuotaToDelete(null);
    };

    const confirmRemoveQuota = async () => {
        if (!quotaToDelete?.id) {
            return;
        }

        setDeletingQuotaId(quotaToDelete.id);

        try {
            await deleteDocRapidoQuotaById(quotaToDelete.id);
            removeQuotaFromForm(quotaToDelete.index);
            setQuotaToDelete(null);
            toast.success('Cota deletada com sucesso!');
        } catch (error) {
            toast.error('Nao foi possivel deletar a cota.');
        } finally {
            setDeletingQuotaId(null);
        }
    };

    const clearInput = () => {
        setForm(createInitialForm());
        setCurrentStep(0);
    };

    const requestClose = () => {
        setConfirmCloseOpen(true);
    };

    const cancelClose = () => {
        setConfirmCloseOpen(false);
    };

    const handleClose = () => {
        clearInput();
        setConfirmCloseOpen(false);
        closeModal();
    };

    const validateCedenteStep = () => {
        if (form.assignorType === 'cpf' && !form.assignorPerson.name) {
            toast.warning('Informe o nome do cedente.');
            return false;
        }

        if (form.assignorType === 'cnpj' && !form.assignorCompany.cnpj) {
            toast.warning('Informe o CNPJ do cedente.');
            return false;
        }

        return true;
    };

    const validateBankStep = () => {
        if (!form.bank.bank) {
            toast.warning('Informe o banco.');
            return false;
        }

        return true;
    };

    const validateAdministratorStep = () => {
        if (!form.administrator.id && !form.administrator.name) {
            toast.warning('Informe o nome da administradora.');
            return false;
        }

        return true;
    };

    const validateQuotaStep = () => {
        if (!form.quotas[0]?.group) {
            toast.warning('Informe ao menos um grupo da cota.');
            return false;
        }

        return true;
    };

    const validateRegistryStep = () => {
        if (!form.registry.notaryCity) {
            toast.warning('Informe a cidade do cartorio.');
            return false;
        }

        return true;
    };

    const validateCurrentStep = () => {
        const validators = [
            validateCedenteStep,
            validateBankStep,
            validateAdministratorStep,
            validateQuotaStep,
            validateRegistryStep,
        ];

        return validators[currentStep]();
    };

    const goToNextStep = () => {
        if (!validateCurrentStep()) {
            return;
        }

        setCurrentStep((step) => Math.min(step + 1, steps.length - 1));
    };

    const goToPreviousStep = () => {
        setCurrentStep((step) => Math.max(step - 1, 0));
    };

    const getDocumentType = (document) => {
        const cleanDocument = String(document || '').replace(/\D/g, '');

        if (!cleanDocument) {
            return '';
        }

        return cleanDocument.length > 11 ? 'CNPJ' : 'CPF';
    };

    const getLoggedConsultantId = () => {
        const token = getCookie(INTERMEDIARY_TOKEN_COOKIE);

        if (!token) {
            return '';
        }

        try {
            const savedToken = typeof token === 'string' && token.startsWith('{')
                ? JSON.parse(token)
                : token;
            const decodedToken = typeof savedToken === 'string'
                ? jwtDecode(savedToken)
                : jwtDecode(savedToken.token);

            return decodedToken?.intermediaryUser?.id
                || decodedToken?.consultantUser?.id
                || decodedToken?.consultant?.id
                || decodedToken?.referrerUser?.id
                || decodedToken?.user?.id
                || decodedToken?.consultant_id
                || decodedToken?.id
                || savedToken?.id
                || '';
        } catch (error) {
            return '';
        }
    };

    const buildPayload = () => {
        const isCpfAssignor = form.assignorType === 'cpf';
        const personData = isCpfAssignor ? form.assignorPerson : form.representative;
        const companyData = form.assignorCompany;

        return {
            consultant_id: getLoggedConsultantId(),
            bank_account_agency: form.bank.agency,
            name: personData.name,
            phone: personData.phone,
            doc_type: isCpfAssignor ? 'CPF' : 'CNPJ',
            cpf_number: isCpfAssignor ? form.assignorPerson.cpf : personData.cpf,
            cnpj_number: isCpfAssignor ? '' : companyData.cnpj,
            nationality: personData.nationality,
            marital_status: personData.maritalStatus,
            occupation: personData.profession,
            address_cep: personData.zipCode,
            address_street: personData.address,
            address_number: personData.number,
            address_neighborhood: personData.neighborhood,
            address_city: personData.city,
            address_state: personData.state,
            address_complement: personData.complement,
            bussiness_name: isCpfAssignor ? '' : companyData.corporateName,
            cnpj_address_cep: isCpfAssignor ? '' : companyData.zipCode,
            cnpj_address_street: isCpfAssignor ? '' : companyData.address,
            cnpj_address_number: isCpfAssignor ? '' : companyData.number,
            cnpj_address_neighborhood: isCpfAssignor ? '' : companyData.neighborhood,
            cnpj_address_city: isCpfAssignor ? '' : companyData.city,
            cnpj_address_state: isCpfAssignor ? '' : companyData.state,
            cnpj_address_complement: isCpfAssignor ? '' : companyData.complement,
            bank_account_id: normalizeId(form.bank.bank),
            bank_account_number: form.bank.account,
            bank_account_type: form.bank.accountType,
            bank_account_name: form.bank.holderName,
            bank_account_doc_number: form.bank.holderDocument,
            bank_account_doc_type: getDocumentType(form.bank.holderDocument),
            key_pix: form.bank.pix,
            key_pix_type: form.bank.pixType,
            consortium_agency_id: form.administrator.id,
            registry_city: form.registry.notaryCity,
            customer_value: formatDecimal(form.registry.assignmentValue),
            check_firma: form.registry.signatureRecognition ? 'S' : 'N',
            status: acquisitionToEdit?.status || 'PENDENTE'
        };
    };

    const buildQuotaPayload = (quota, acquisitionId) => ({
        docrapido_quotasacquisition_id: acquisitionId,
        group_number: quota.group,
        quota_number: quota.quota,
        contract_number: quota.contract,
        credit_value: formatDecimal(quota.creditValue),
        paid_percentage_value: quota.commonFundPercent,
        receivable_value: formatDecimal(quota.receivableValue),
    });

    const saveRecommendation = async () => {
        const acquisition = await newDocRapidoQuotasAcquisition(buildPayload());

        await Promise.all(
            form.quotas.map((quota) => newDocRapidoQuota(buildQuotaPayload(quota, acquisition.id)))
        );

        return acquisition;
    };

    const updateRecommendation = async () => {
        const acquisition = await editDocRapidoQuotasAcquisitionById(acquisitionToEdit.id, buildPayload());

        await Promise.all(
            form.quotas.map((quota) => {
                const quotaPayload = buildQuotaPayload(quota, acquisitionToEdit.id);

                if (quota.id) {
                    return editDocRapidoQuotaById(quota.id, quotaPayload);
                }

                return newDocRapidoQuota(quotaPayload);
            })
        );

        return acquisition;
    };

    async function addRecommendation() {
        if (!validateCurrentStep()) {
            return false;
        }

        const promise = isEditMode ? updateRecommendation() : saveRecommendation();

        toast.promise(
            promise,
            {
                pending: 'Aguarde...',
                success: isEditMode ? 'Intermediacao atualizada com sucesso!' : 'Cadastro realizado com sucesso!',
                error: isEditMode ? 'Erro ao atualizar' : 'Erro ao cadastrar',
            }
        );

        const response = await promise;

        if (response) {
            clearInput();
            closeModal();
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={requestClose}
            contentLabel="Cadastro de indicacao"
            className="add-intermediary-modal"
            shouldCloseOnOverlayClick={true}
            style={{
                overlay: {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1000,
                },
                content: {
                    zIndex: 1001,
                },
            }}
        >
            <div className="add-intermediary-header">
                <h1>{isEditMode ? 'Editar lançamento' : 'Novo lançamento'}</h1>
                <button type="button" className="add-intermediary-close" onClick={requestClose}>X</button>
            </div>

            <div className="add-intermediary-steps">
                {steps.map((step, index) => (
                    <button
                        type="button"
                        key={step}
                        className={`add-intermediary-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'done' : ''}`}
                        onClick={() => setCurrentStep(index)}
                    >
                        <span>{index + 1}</span>
                        {step}
                    </button>
                ))}
            </div>

            <div className="add-intermediary-body">
                {loadingEditData && (
                    <section className="add-intermediary-section">
                        <span>Carregando dados da intermediacao...</span>
                    </section>
                )}

                {!loadingEditData && currentStep === 0 && (
                    <section className="add-intermediary-section">
                        <div className="add-intermediary-section-title">
                            <h2>Cedente</h2>
                            <div className="add-intermediary-segmented">
                                <button
                                    type="button"
                                    className={form.assignorType === 'cpf' ? 'active' : ''}
                                    onClick={() => updateForm('assignorType', 'cpf')}
                                >
                                    CPF
                                </button>
                                <button
                                    type="button"
                                    className={form.assignorType === 'cnpj' ? 'active' : ''}
                                    onClick={() => updateForm('assignorType', 'cnpj')}
                                >
                                    CNPJ
                                </button>
                            </div>
                        </div>

                        {form.assignorType === 'cpf' ? (
                            <PersonFields
                                values={form.assignorPerson}
                                onChange={(field, value) => updateSection('assignorPerson', field, value)}
                                onZipCodeBlur={(zipCode) => fillAddressByZipCode('assignorPerson', zipCode)}
                                nameLabel="Nome"
                                cityLabel="Cidade"
                                addressTitle="Endereço"
                            />
                        ) : (
                            <>
                                <CompanyFields
                                    values={form.assignorCompany}
                                    onChange={(field, value) => updateSection('assignorCompany', field, value)}
                                    onZipCodeBlur={(zipCode) => fillAddressByZipCode('assignorCompany', zipCode)}
                                    includeName={false}
                                    addressTitle="Dados de endereço CNPJ"
                                />

                                <div className="add-intermediary-subtitle">Representante</div>
                                <PersonFields
                                    values={form.representative}
                                    onChange={(field, value) => updateSection('representative', field, value)}
                                    onZipCodeBlur={(zipCode) => fillAddressByZipCode('representative', zipCode)}
                                    nameLabel="Nome"
                                    cityLabel="Cidade"
                                    addressTitle="Endereço (Representante)"
                                />
                            </>
                        )}
                    </section>
                )}

                {!loadingEditData && currentStep === 1 && (
                    <section className="add-intermediary-section">
                        <h2>Dados bancários</h2>
                        <CheckboxInput
                            label="Utilizar dados do Favorecido para o pagamento"
                            checked={form.bank.useFavoredDataForPayment}
                            onChange={toggleUseFavoredDataForPayment}
                            variant="subtitle"
                        />
                        <div className="add-intermediary-grid add-intermediary-bank-grid">
                            <BankSelect banks={banks} value={form.bank.bank} onChange={(value) => updateSection('bank', 'bank', value)} wide={true} />
                            <TextInput label="Agência" value={form.bank.agency} onChange={(value) => updateSection('bank', 'agency', value)} />
                            <TextInput label="Conta" value={form.bank.account} onChange={(value) => updateSection('bank', 'account', value)} />
                            <TextInput label="Nome titular" value={form.bank.holderName} onChange={(value) => updateSection('bank', 'holderName', value)} wide={true} />
                            <TextInput label="Documento" value={form.bank.holderDocument} onChange={(value) => updateSection('bank', 'holderDocument', value)} />
                            <SelectInput label="Tipo de Conta" value={form.bank.accountType} onChange={(value) => updateSection('bank', 'accountType', value)} options={['Corrente', 'Poupanca', 'Pagamento']} />
                            <SelectInput label="Tipo chave Pix" value={form.bank.pixType} onChange={updatePixType} options={['CPF', 'CNPJ', 'Telefone', 'Email', 'Aleatoria']} />
                            <PixInput pixType={form.bank.pixType} value={form.bank.pix} onChange={(value) => updateSection('bank', 'pix', value)} />
                        </div>
                    </section>
                )}

                {!loadingEditData && currentStep === 2 && (
                    <section className="add-intermediary-section">
                        <h2>Administradora</h2>
                        <AdministratorFields
                            administrators={administrators}
                            values={form.administrator}
                            onChange={(field, value) => updateSection('administrator', field, value)}
                            onSelectAdministrator={selectAdministrator}
                            onZipCodeBlur={(zipCode) => fillAddressByZipCode('administrator', zipCode)}
                        />
                    </section>
                )}

                {!loadingEditData && currentStep === 3 && (
                    <section className="add-intermediary-section">
                        <div className="add-intermediary-section-title">
                            <h2>Dados da cota</h2>
                            <button type="button" className="add-intermediary-small-btn" onClick={addQuota}>Adicionar cota</button>
                        </div>

                        {form.quotas.map((quota, index) => (
                            <div className="add-intermediary-quota" key={`quota-${index}`}>
                                <div className="add-intermediary-quota-title">
                                    <span>Cota {index + 1}</span>
                                    {form.quotas.length > 1 && (
                                        <button
                                            type="button"
                                            className="add-intermediary-remove-btn"
                                            onClick={() => requestRemoveQuota(index)}
                                            disabled={deletingQuotaId === quota.id}
                                        >
                                            {deletingQuotaId === quota.id ? 'Deletando...' : 'Remover'}
                                        </button>
                                    )}
                                </div>
                                <div className="add-intermediary-quota-grid">
                                    <TextInput label="Grupo" value={quota.group} onChange={(value) => updateQuota(index, 'group', value)} />
                                    <TextInput label="Cota" value={quota.quota} onChange={(value) => updateQuota(index, 'quota', value)} />
                                    <TextInput label="Contrato" value={quota.contract} onChange={(value) => updateQuota(index, 'contract', value)} />
                                    <MoneyInput label="Valor crédito" value={quota.creditValue} onChange={(value) => updateQuota(index, 'creditValue', value)} />
                                    <MaskedInput label="% fundo comum" mask="99,9999" value={quota.commonFundPercent} onChange={(value) => updateQuota(index, 'commonFundPercent', value)} />
                                    <TextInput label="Valor recebível" value={quota.receivableValue} onChange={() => { }} readOnly={true} />
                                </div>
                            </div>
                        ))}
                    </section>
                )}

                {!loadingEditData && currentStep === 4 && (
                    <section className="add-intermediary-section">
                        <h2>Cartório e cessão</h2>
                        <div className="add-intermediary-grid">
                            <TextInput label="Cidade (cartório)" value={form.registry.notaryCity} onChange={(value) => updateSection('registry', 'notaryCity', value)} />
                            <MoneyInput label="Valor da cessão" value={form.registry.assignmentValue} onChange={(value) => updateSection('registry', 'assignmentValue', value)} />
                            <CheckboxInput
                                label="Reconhecimento de firma"
                                checked={form.registry.signatureRecognition}
                                onChange={(value) => updateSection('registry', 'signatureRecognition', value)}
                            />
                        </div>
                    </section>
                )}
            </div>

            <div className="add-intermediary-footer">
                
                {currentStep > 0 && (
                    <button type="button" className="add-intermediary-back" onClick={goToPreviousStep}>Voltar</button>
                )}
                {currentStep < steps.length - 1 ? (
                    <button type="button" className="add-intermediary-submit" onClick={goToNextStep} disabled={loadingEditData}>Proximo</button>
                ) : (
                    <button type="button" className="add-intermediary-submit" onClick={addRecommendation} disabled={loadingEditData}>
                        {isEditMode ? 'Salvar' : 'Indicar'}
                    </button>
                )}
            </div>
            {confirmCloseOpen && (
                <div className="add-intermediary-confirm-overlay">
                    <div className="add-intermediary-confirm-modal">
                        <h3>Descartar dados?</h3>
                        <p>Se fechar agora, os dados preenchidos serao perdidos.</p>
                        <div className="add-intermediary-confirm-actions">
                            <button type="button" className="add-intermediary-back" onClick={cancelClose}>Continuar preenchendo</button>
                            <button type="button" className="add-intermediary-danger" onClick={handleClose}>Descartar</button>
                        </div>
                    </div>
                </div>
            )}
            {quotaToDelete && (
                <div className="add-intermediary-confirm-overlay">
                    <div className="add-intermediary-confirm-modal add-intermediary-confirm-modal-small">
                        <h3>Deletar cota?</h3>
                        <p>
                            Esta acao removera a cota
                            {' '}
                            {quotaToDelete.quota ? `${quotaToDelete.quota}` : `#${quotaToDelete.index + 1}`}
                            {' '}
                            permanentemente.
                        </p>
                        <div className="add-intermediary-confirm-actions">
                            <button
                                type="button"
                                className="add-intermediary-back"
                                onClick={cancelRemoveQuota}
                                disabled={deletingQuotaId === quotaToDelete.id}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="add-intermediary-danger"
                                onClick={confirmRemoveQuota}
                                disabled={deletingQuotaId === quotaToDelete.id}
                            >
                                {deletingQuotaId === quotaToDelete.id ? 'Deletando...' : 'Deletar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
}

function PersonFields({ values, onChange, onZipCodeBlur, nameLabel, cityLabel, addressTitle = 'Dados de endereço' }) {
    return (
        <div className="add-intermediary-grid add-intermediary-person-grid">
            <TextInput label={nameLabel} value={values.name} onChange={(value) => onChange('name', value)} wide={true} />
            <MaskedInput label="CPF" mask="999.999.999-99" value={values.cpf} onChange={(value) => onChange('cpf', value)} />
            <MaskedInput label="Telefone" mask="(99) 99999-9999" value={values.phone} onChange={(value) => onChange('phone', value)} />
            <TextInput label="Nacionalidade" value={values.nationality} onChange={(value) => onChange('nationality', value)} />
            <TextInput label="Estado Civil" value={values.maritalStatus} onChange={(value) => onChange('maritalStatus', value)} />
            <TextInput label="Profissão" value={values.profession} onChange={(value) => onChange('profession', value)} />
            <div className="add-intermediary-address-title">{addressTitle}</div>
            <MaskedInput label="CEP" mask="99999-999" value={values.zipCode} onChange={(value) => onChange('zipCode', value)} onBlur={() => onZipCodeBlur(values.zipCode)} />
            <TextInput label="Rua" value={values.address} onChange={(value) => onChange('address', value)} wide={true} />
            <TextInput label="Número" value={values.number} onChange={(value) => onChange('number', value)} />
            <TextInput label="Complemento" value={values.complement} onChange={(value) => onChange('complement', value)} />
            <TextInput label="Bairro" value={values.neighborhood} onChange={(value) => onChange('neighborhood', value)} />
            <TextInput label={cityLabel} value={values.city} onChange={(value) => onChange('city', value)} />
            <SelectInput label="Estado" value={values.state} onChange={(value) => onChange('state', value)} options={brazilianStates} />
        </div>
    );
}

function CompanyFields({ values, onChange, onZipCodeBlur, includeName, nameLabel, addressTitle = 'Dados de endereço' }) {
    return (
        <div className="add-intermediary-grid add-intermediary-company-grid">
            {includeName && (
                <TextInput label={nameLabel} value={values.name} onChange={(value) => onChange('name', value)} />
            )}
            <MaskedInput label="CNPJ" mask="99.999.999/9999-99" value={values.cnpj} onChange={(value) => onChange('cnpj', value)} />
            {!includeName && (
                <TextInput label="Razão Social" value={values.corporateName} onChange={(value) => onChange('corporateName', value)} />
            )}
            <div className="add-intermediary-address-title">{addressTitle}</div>
            <MaskedInput label="CEP" mask="99999-999" value={values.zipCode} onChange={(value) => onChange('zipCode', value)} onBlur={() => onZipCodeBlur(values.zipCode)} />
            <TextInput label="Rua" value={values.address} onChange={(value) => onChange('address', value)} wide={true} />
            <TextInput label="Número" value={values.number} onChange={(value) => onChange('number', value)} />
            <TextInput label="Complemento" value={values.complement} onChange={(value) => onChange('complement', value)} />
            <TextInput label="Bairro" value={values.neighborhood} onChange={(value) => onChange('neighborhood', value)} />
            <TextInput label="Cidade" value={values.city} onChange={(value) => onChange('city', value)} />
            <SelectInput label="Estado" value={values.state} onChange={(value) => onChange('state', value)} options={brazilianStates} />
        </div>
    );
}

function AdministratorFields({ administrators, values, onChange, onSelectAdministrator, onZipCodeBlur }) {
    return (
        <div className="add-intermediary-grid add-intermediary-administrator-grid" onKeyDown={(event) => event.target.tagName === 'INPUT' && event.preventDefault()}>
            <label className="add-intermediary-field">
                <span>Nome administradora</span>
                <select value={String(values.id || values.name || '')} onChange={(event) => onSelectAdministrator(event.target.value)}>
                    <option value="">Selecione</option>
                    {administrators.map((administrator) => (
                        <option key={administrator.id || administrator.name} value={String(administrator.id || administrator.name)}>
                            {administrator.name}
                        </option>
                    ))}
                </select>
            </label>
            <MaskedInput label="CNPJ" mask="99.999.999/9999-99" value={values.cnpj} onChange={(value) => onChange('cnpj', value)} readOnly={true} />
            <MaskedInput label="CEP" mask="99999-999" value={values.zipCode} onChange={(value) => onChange('zipCode', value)} onBlur={() => onZipCodeBlur(values.zipCode)} />
            <TextInput label="Rua" value={values.address} onChange={(value) => onChange('address', value)} wide={true} />
            <TextInput label="Número" value={values.number} onChange={(value) => onChange('number', value)} />
            <TextInput label="Bairro" value={values.neighborhood} onChange={(value) => onChange('neighborhood', value)} />
            <TextInput label="Cidade" value={values.city} onChange={(value) => onChange('city', value)} />
            <TextInput label="Estado" value={values.state} onChange={(value) => onChange('state', value)} readOnly={true} />
        </div>
    );
}

function TextInput({ label, value, onChange, readOnly = false, wide = false }) {
    return (
        <label className={`add-intermediary-field ${wide ? 'add-intermediary-field-wide' : ''}`}>
            <span>{label}</span>
            <input type="text" value={value} onChange={(event) => onChange(event.target.value)} readOnly={readOnly} />
        </label>
    );
}

function MaskedInput({ label, value, onChange, onBlur, mask, readOnly = false }) {
    return (
        <label className="add-intermediary-field">
            <span>{label}</span>
            <InputMask mask={mask} maskChar={null} value={value} onChange={(event) => onChange(event.target.value)} onBlur={onBlur} readOnly={readOnly} />
        </label>
    );
}

function PixInput({ pixType, value, onChange }) {
    const masks = {
        CPF: '999.999.999-99',
        CNPJ: '99.999.999/9999-99',
        Telefone: '(99) 99999-9999',
    };
    const mask = masks[pixType];

    if (mask) {
        return (
            <MaskedInput
                label="Pix"
                mask={mask}
                value={value}
                onChange={onChange}
            />
        );
    }

    return (
        <TextInput
            label="Pix"
            value={value}
            onChange={onChange}
        />
    );
}

function MoneyInput({ label, value, onChange }) {
    return (
        <label className="add-intermediary-field">
            <span>{label}</span>
            <NumericFormat
                value={value}
                onValueChange={({ formattedValue }) => onChange(formattedValue)}
                decimalSeparator=","
                thousandSeparator="."
                prefix="R$ "
                fixedDecimalScale={true}
                decimalScale={2}
                allowNegative={false}
            />
        </label>
    );
}

function SelectInput({ label, value, onChange, options }) {
    return (
        <label className="add-intermediary-field">
            <span>{label}</span>
            <select value={value} onChange={(event) => onChange(event.target.value)}>
                <option value="">Selecione</option>
                {options.map((option) => (
                    <option key={option} value={option}>{option}</option>
                ))}
            </select>
        </label>
    );
}

function BankSelect({ banks, value, onChange, wide = false }) {
    return (
        <label className={`add-intermediary-field ${wide ? 'add-intermediary-field-wide' : ''}`}>
            <span>Banco</span>
            <select value={value} onChange={(event) => onChange(event.target.value)}>
                <option value="">Selecione</option>
                {banks.map((bank) => (
                    <option key={bank.id} value={bank.id}>{bank.name}</option>
                ))}
            </select>
        </label>
    );
}

function CheckboxInput({ label, checked, onChange, variant = '' }) {
    return (
        <label className={`add-intermediary-checkbox ${variant === 'subtitle' ? 'add-intermediary-checkbox-subtitle' : ''}`}>
            <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
            <span>{label}</span>
        </label>
    );
}

export default AddRecommendationIntermediary;
